import {WatchlistListsModel} from '../domain/watchlist/WatchlistDomains';
import {CustomListModel} from '../domain/custom-list/CustomListDomains';
import ServiceContainer from '../config/ServiceContainer';
import {ServiceNames} from '../constant/ServiceNames';
import CustomListService from '../service/CustomListService';
import WatchlistUtil from './WatchlistUtil';

export interface CustomListSourceDisplay {
  name: string;
  size: number;
}

class WatchlistCustomListSourceLoader {
  public static async load(
    watchlistIds: string[],
    watchlistResponse: WatchlistListsModel[],
    preferredSource?: CustomListSourceDisplay,
  ): Promise<Record<string, CustomListSourceDisplay>> {
    const uniqueIds: string[] = Array.from(new Set(watchlistIds.filter((listId: string) => listId != undefined && listId.length > 0)));
    if (uniqueIds.length === 0) {
      return {};
    }

    const customListService: CustomListService = ServiceContainer.getInstance().get(ServiceNames.CUSTOM_LIST);
    let customLists: CustomListModel[] = [];
    try {
      customLists = await customListService.getAll();
    } catch (e) {
      console.error(e);
      return this.buildPreferredFallback(uniqueIds, watchlistResponse, preferredSource);
    }

    const customListsById: Map<string, CustomListModel> = new Map(
      customLists.map((list: CustomListModel) => [list.id, list]),
    );
    const pathname: string = window.location.pathname;
    const pathListId: string | undefined = WatchlistUtil.getCustomListIdFromPath(pathname);
    const pathParams: URLSearchParams = WatchlistUtil.getPathSearchParams(pathname);
    const urlListIds: string[] = this.parseUrlListIds(pathParams);
    const recordId: string | undefined = pathParams.get('recordId') ?? undefined;
    let listsByRecord: CustomListModel[] | undefined = undefined;
    const sources: Record<string, CustomListSourceDisplay> = {};

    for (const watchlistId of uniqueIds) {
      const engineList: WatchlistListsModel | undefined = watchlistResponse.find((list: WatchlistListsModel) => list.id == watchlistId);
      if (this.isKnownNonCustomList(engineList)) {
        continue;
      }

      let resolvedId: string | undefined = this.resolveListId(watchlistId, engineList, customListsById, pathListId, urlListIds);
      if (resolvedId == undefined && recordId != undefined && recordId.length > 0) {
        if (listsByRecord == undefined) {
          try {
            listsByRecord = await customListService.findListsByRecordId(recordId);
          } catch (e) {
            console.error(e);
            listsByRecord = [];
          }
        }
        resolvedId = this.resolveFromRecordLists(listsByRecord, urlListIds, pathListId);
      }
      if (resolvedId == undefined && preferredSource != undefined) {
        sources[watchlistId] = preferredSource;
        continue;
      }
      if (resolvedId == undefined) {
        continue;
      }

      const list: CustomListModel | undefined = customListsById.get(resolvedId);
      if (list == undefined) {
        if (preferredSource != undefined) {
          sources[watchlistId] = preferredSource;
        }
        continue;
      }
      const size: number = preferredSource != undefined
        ? preferredSource.size
        : await customListService.getRecordCount(resolvedId);
      sources[watchlistId] = {name: list.name, size};
    }
    return sources;
  }

  private static buildPreferredFallback(
    uniqueIds: string[],
    watchlistResponse: WatchlistListsModel[],
    preferredSource: CustomListSourceDisplay | undefined,
  ): Record<string, CustomListSourceDisplay> {
    if (preferredSource == undefined) {
      return {};
    }
    const sources: Record<string, CustomListSourceDisplay> = {};
    for (const watchlistId of uniqueIds) {
      const engineList: WatchlistListsModel | undefined = watchlistResponse.find((list: WatchlistListsModel) => list.id == watchlistId);
      if (this.isKnownNonCustomList(engineList)) {
        continue;
      }
      sources[watchlistId] = preferredSource;
    }
    return sources;
  }

  private static parseUrlListIds(pathParams: URLSearchParams): string[] {
    const rawLists: string = pathParams.get('lists') ?? '';
    return rawLists
      .split(',')
      .map((listId: string) => listId.trim())
      .filter((listId: string) => listId.length > 0);
  }

  private static resolveListId(
    watchlistId: string,
    engineList: WatchlistListsModel | undefined,
    customListsById: Map<string, CustomListModel>,
    pathListId: string | undefined,
    urlListIds: string[],
  ): string | undefined {
    if (customListsById.has(watchlistId)) {
      return watchlistId;
    }
    if (pathListId != undefined && customListsById.has(pathListId)) {
      return pathListId;
    }
    const urlCustomListIds: string[] = urlListIds.filter((listId: string) => customListsById.has(listId));
    if (urlCustomListIds.length === 1) {
      return urlCustomListIds[0];
    }
    if (urlCustomListIds.length > 1 && WatchlistUtil.isCustomList(engineList)) {
      return urlCustomListIds[0];
    }
    return undefined;
  }

  private static isKnownNonCustomList(engineList: WatchlistListsModel | undefined): boolean {
    return engineList != undefined && !WatchlistUtil.isCustomList(engineList);
  }

  private static resolveFromRecordLists(
    listsByRecord: CustomListModel[],
    urlListIds: string[],
    pathListId: string | undefined,
  ): string | undefined {
    if (listsByRecord.length === 0) {
      return undefined;
    }
    if (pathListId != undefined && listsByRecord.some((list: CustomListModel) => list.id === pathListId)) {
      return pathListId;
    }
    const matchedUrlIds: string[] = urlListIds.filter((listId: string) => listsByRecord.some((list: CustomListModel) => list.id === listId));
    if (matchedUrlIds.length > 0) {
      return matchedUrlIds[0];
    }
    return listsByRecord[0].id;
  }
}

export default WatchlistCustomListSourceLoader;
